



















































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { LazyLoader } from "../../../../../base/components/system";
import { ModelEditableMixin } from "../../../../../base/components/system/mixins";
import { IBehaviorDocumentEditorObjectLazyDrawerConfiguration } from "../../../../../base/domain/configuration";
import { Point, Size } from "../../../../../base/domain/drawing";
import { DomainRegistry } from "../../../../domain";
import { IConfiguration } from "../../../../domain/configuration";
import { IFormatObjectEditable, IFormatObjectTemporaryImage, FormatPageStructureEditor } from "../../../../domain/editableFormat";
import { FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin, UploadImageFileMixin, QuestionnaireGuidAssignableMixin } from "../mixins";
import { QuestionnaireFormatPageInputTracker, FormatPageObjectDragTracker, QuestionnaireFormatPageLocator, IFormatPageLocatable, QuestionnaireFormatPageRelocator } from "./trackers";
import { default as QuestionnaireFormatPageObjectDrawer } from "./QuestionnaireFormatPageObjectDrawer.vue";
import { default as FormatPageObjectMenu } from "./FormatPageObjectMenu.vue";
import { instanceOfISizePropertyEditable } from "../../../../domain/editableFormat/properties";
import { QuestionnaireGuidTreeEditor, QuestionnaireGuidTree } from "../../../../domain/questionnaire";
import { QuestionnaireFormatObject } from "../../../../domain/questionnaire/constants";

@Component({
  components: {
    QuestionnaireFormatPageInputTracker,
    QuestionnaireFormatPageObjectDrawer,
    FormatPageObjectMenu,
    FormatPageObjectDragTracker,
    QuestionnaireFormatPageLocator,
    QuestionnaireFormatPageRelocator,
    LazyLoader,
  }
})
export default class QuestionnaireFormatPageEditor extends Mixins(ModelEditableMixin, FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin, UploadImageFileMixin, QuestionnaireGuidAssignableMixin) {
  writePropertyWhenValid: (writer: (source: FormatPageStructureEditor) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: FormatPageStructureEditor) => T, defaultValue: T) => T;

  @Prop({ default: 1 })
  scale: number;

  @Prop({ default: () => new Size(10, 10) })
  gridSize: Size;

  @Prop()
  isActive: boolean;

  $refs!: {
    page: HTMLDivElement,
    menu: FormatPageObjectMenu,
    objectDragTracker: FormatPageObjectDragTracker,
    locator: QuestionnaireFormatPageLocator,
    relocator: QuestionnaireFormatPageRelocator,
    lazyLoader: LazyLoader,
  };
  $configuration!: IConfiguration;

  data() {
    return {
      pageElement: null as HTMLDivElement,
      drawableObjects: [] as IFormatObjectEditable[],
      appenderManager: { index: -1, isHit: false },
      relocatorManager: { index: -1, isHit: false },
      isRelocating: false,
    };
  }
  pageElement: HTMLDivElement;
  drawableObjects: IFormatObjectEditable[];
  appenderManager: { index: number, isHit: boolean };
  relocatorManager: { index: number, isHit: boolean };
  isRelocating: boolean;

  mounted() {
    this.pageElement = this.$refs.page;
  }

  updated() {
    this.$refs.lazyLoader.loadNext();
  }

  get currentDrawableObjects() {
    return this.drawableObjects.filter(o => !this.isRelocating || !this.selectedObjects.includes(o));
  }

  toDrawableObjectIndex(index: number) {
    const found = this.drawableObjects.findIndex(o => o === this.currentDrawableObjects[index]);
    return found < 0 ? -1 : found;
  }

  get treeItems(): QuestionnaireGuidTree<IFormatObjectEditable>[] {
    return QuestionnaireGuidTree.from(this.drawableObjects);
  }

  get objectContainerStyle() {
    return {
      padding: `0 ${QuestionnaireFormatObject.Gap}px`,
    };
  }

  toLocatorStyle(index: number) {
    const height = [
      this.toLocatorSourceHeight(index, this.$refs.locator.sourceHeight, this.appenderManager.index, this.appenderManager.isHit),
      this.toLocatorSourceHeight(index, this.$refs.relocator.sourceHeight, this.relocatorManager.index, this.relocatorManager.isHit),
      QuestionnaireFormatObject.Gap
    ].find(h => typeof h === "number");
    return {
      height: `${height}px`,
    };
  }

  private toLocatorSourceHeight(index: number, sourceHeight: number, managerIndex: number, managerIsHit: boolean): number {
    return (typeof sourceHeight === "number" && managerIndex === index && !managerIsHit)
      ? sourceHeight + (QuestionnaireFormatObject.Gap * 2)
      : null;
  }

  get lazyDrawerConfiguration(): IBehaviorDocumentEditorObjectLazyDrawerConfiguration {
    return this.$configuration.formatEditor.behavior.format.editor.objectLazyDrawer;
  }

  @Watch("editingSource")
  onEditingSourceUpdated(newValue: FormatPageStructureEditor, oldValue: FormatPageStructureEditor) {
    if (!newValue.isReferedSameSource(oldValue)) {
      this.$refs.lazyLoader.restart();
    }
  }

  getLocator(): IFormatPageLocatable {
    return this.$refs.locator;
  }

  get pageSizeStyleProperties() {
    const image = this.image;
    if (!image) {
      return null;
    }
    const isHorizontal = this.isHorizontal;
    return {
      width: (isHorizontal ? image.size.height : image.size.width) + this.toLevelWidth() + "px",
      height: Math.max((isHorizontal ? image.size.width : image.size.height), (this.totalHeightOfObjectsFits + (QuestionnaireFormatObject.Gap * 3))) + "px"
    };
  }

  private toLevelWidth(): number {
    return Math.max(0, ...this.treeItems.map(v => v.level)) * QuestionnaireFormatObject.Gap;
  }

  get totalHeightOfObjectsFits(): number {
    // チェックボックスは取得したサイズより2px大きく表示されているため、ページの高さやオブジェクト追加可能位置の描画がずれてしまう
    return this.objectHeights.reduce((p, c) => p += c + QuestionnaireFormatObject.Gap, 0) + (QuestionnaireFormatObject.Gap * 2);
  }

  get objectHeights(): number[] {
    return this.currentDrawableObjects.map(v => instanceOfISizePropertyEditable(v) ? v.size().getValue().height : 0);
  }

  toObjectIndex(location: Point): number {
    let index = 0;
    let y = 0;
    for (; index < this.objectHeights.length; index++) {
      const height = this.objectHeights[index];
      if (location.y < (y + (height / 2) + QuestionnaireFormatObject.Gap)) {
        break;
      }
      y += height + QuestionnaireFormatObject.Gap;
    }
    return index;
  }

  get layerRectangleStyleProperties() {
    const layer = this.layer;
    if (!layer || !layer.imageGuid) {
      return null;
    }
    return {
      left: layer.location.x + "px",
      top: layer.location.y + "px",
      width: layer.size.width + "px",
      height: layer.size.height + "px"
    };
  }

  get layerSource(): string {
    const layer = this.layer;
    if (!layer || !layer.imageGuid) {
      return null;
    }
    const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
    const imageSource = layer.formatId
      ? repository.getLayerImageSource(layer.formatId, layer.imageGuid)
      : repository.getTemporaryImageSource(layer.imageGuid);
    return imageSource.source;
  }

  get temporaryImages(): IFormatObjectTemporaryImage[] {
    return this.storePresenter.getTemporaryImages();
  }

  onDragHandleSelected(source: IFormatObjectEditable, event: PointerEvent): void {
    event.stopPropagation();
    if (this.$refs.menu.shouldShow) {
      return;
    }
    this.isRelocating = true;
    const isSelected = source.isSelected();
    if (!isSelected) {
      this.reselectObjects(source);
    }
    const baseSource = isSelected
      ? this.drawableObjects.find(v => v.isSelected())
      : source;
    const childrenObjects = this.treeItems
      .filter(v => v.source.isSelected())
      .map(v => v.toChildren())
      .reduce((c, v) => c.concat(v))
      .map(v => v.source);
    this.selectObjects(...childrenObjects);
    this.$refs.relocator.layoutRectangleObject(this, baseSource)
      .then(r => {
        if (!r || typeof r.index !== "number" || r.index === -1) {
          return;
        }
        this.writePropertyWhenValid(s => {
          new QuestionnaireGuidTreeEditor(s).insertSelectedObjects(r.isHitObject, r.index);
        });
      })
      .finally(() => this.isRelocating = false);
  }

  onObjectMouseDownCaptured(sender: IFormatObjectEditable, event: PointerEvent): void {
    this.$refs.menu.handleMouseDownCaptured(sender, event);
  }

  onPageMouseRightDownHandled(event: PointerEvent): void {
    this.$refs.menu.handlePageMouseRightDowned(event);
  }
}
